import { normalize } from './dates';
import type {
  Address,
  AvailState,
  Blackout,
  ChurchStatus,
  CivilStatus,
  EmergencyContact,
  JoinedVia,
  Member,
} from './types';

// Um `Intl.Collator` só, reaproveitado: o `localeCompare` com locale monta um a cada
// comparação e ordenar 100 nomes passava de 3 ms — a cada tecla, nos campos que reordenam
// a lista de membros.
const NAME_ORDER = new Intl.Collator('pt-BR', { sensitivity: 'base' });
const TEXT_ORDER = new Intl.Collator('pt-BR');

/** Ordem alfabética de nomes em pt-BR, sem diferenciar acento nem maiúscula. */
export function compareNames(a: string, b: string): number {
  return NAME_ORDER.compare(a, b);
}

/** Ordem alfabética em pt-BR (a do `localeCompare(b, 'pt-BR')`), com o collator reaproveitado. */
export function compareText(a: string, b: string): number {
  return TEXT_ORDER.compare(a, b);
}

export function sortByName<T extends { name: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => compareNames(a.name, b.name));
}

/**
 * Erro do nome no cadastro: obrigatório e sem repetir o de outro membro (comparado sem
 * acento, maiúscula nem espaço nas pontas). `null` quando está válido.
 */
export function memberNameError(members: readonly Member[], memberId: string, name: string): string | null {
  const typed = normalize(name.trim());

  if (!typed) return 'Informe o nome do membro.';
  if (members.some((m) => m.id !== memberId && normalize(m.name.trim()) === typed)) {
    return 'Já existe um membro com esse nome.';
  }

  return null;
}

/** E-mail como é guardado: sem espaço nas pontas e em minúsculas. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Erro do e-mail no cadastro: opcional, mas, se informado, válido e sem repetir o de outro
 * membro (é o e-mail do login). `null` quando está válido.
 */
export function memberEmailError(members: readonly Member[], memberId: string, email: string): string | null {
  const typed = normalizeEmail(email);

  if (!typed) return null;
  if (!EMAIL.test(typed) || typed.length > 254) return 'Informe um e-mail válido.';
  if (members.some((m) => m.id !== memberId && normalizeEmail(m.email) === typed)) {
    return 'Este e-mail já é de outro membro.';
  }

  return null;
}

/** Telefone como é guardado: só os dígitos (DDD + número), sem o +55. */
export function phoneDigits(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  // Colado com o código do país ("+55 11 91234-5678").
  const national = digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;

  return national.slice(0, 11);
}

/**
 * Máscara enquanto se digita: "(11) 91234-5678" no celular, "(11) 3456-7890" no fixo.
 * Aceita texto já formatado ou só dígitos.
 */
export function formatPhone(phone: string): string {
  const d = phoneDigits(phone);

  if (!d) return '';
  if (d.length <= 2) return `(${d}`;

  const ddd = d.slice(0, 2);
  const rest = d.slice(2);
  // O hífen fica depois de 4 dígitos no fixo (8) e de 5 no celular (9).
  const cut = rest.length > 8 ? 5 : 4;

  return rest.length > 4 ? `(${ddd}) ${rest.slice(0, cut)}-${rest.slice(cut)}` : `(${ddd}) ${rest}`;
}

/** DDD sem zero; fixo começa com 2 a 8, celular com 9 (o mesmo `CHECK` do banco). */
export const PHONE = /^[1-9][1-9]([2-8]\d{7}|9\d{8})$/;

/**
 * Erro do telefone no cadastro: opcional, mas, se informado, com DDD e 10 (fixo) ou 11
 * dígitos (celular, que começa com 9 depois do DDD). `null` quando está válido.
 */
export function memberPhoneError(phone: string): string | null {
  const d = phoneDigits(phone);

  if (!d) return null;
  if (d.length !== 10 && d.length !== 11) return 'Informe o telefone com DDD: 10 ou 11 dígitos.';
  if (d.length === 11 && d[2] !== '9') return 'Celular com 11 dígitos começa com 9 depois do DDD.';
  if (!PHONE.test(d)) return 'Informe um telefone válido, com DDD.';

  return null;
}

/** Período da ausência programada: as duas datas, e o fim igual ou depois do início. */
export function blackoutError(from: string, to: string): string | null {
  if (!from || !to) return 'Informe as datas de início e fim.';
  if (to < from) return 'A data final deve ser igual ou posterior à inicial.';

  return null;
}

/** O rascunho do perfil difere do cadastro salvo? */
export function memberChanged(saved: Member, draft: Member): boolean {
  return JSON.stringify(saved) !== JSON.stringify(draft);
}

/**
 * Alterna a disponibilidade. Uma célula alterna Disponível › Indisponível › Não
 * configurado; uma linha ou coluna inteira vira Disponível, ou Indisponível se já estava
 * toda disponível. `value` força o valor (atalhos).
 */
export function toggleAvailability(member: Member, indexes: number[], value?: AvailState): Member {
  return { ...member, availability: toggleSlots(member.availability, indexes, value) };
}

/**
 * A mesma alternância sobre a grade solta (a "Aplicar disponibilidade" do Editor de
 * Membros): um período gira Disponível › Indisponível › Não configurado; vários (a linha ou
 * a coluna) ficam todos disponíveis, ou todos indisponíveis se já estavam.
 */
export function toggleSlots(
  slots: readonly AvailState[],
  indexes: number[],
  value?: AvailState
): AvailState[] {
  const cycle: Record<AvailState, AvailState> = {
    available: 'unavailable',
    unavailable: 'unset',
    unset: 'available',
  };
  const availability = [...slots];
  const bulk: AvailState = indexes.every((i) => availability[i] === 'available')
    ? 'unavailable'
    : 'available';

  indexes.forEach((i) => {
    availability[i] = value ?? (indexes.length === 1 ? cycle[availability[i] ?? 'unset'] : bulk);
  });

  return availability;
}

/** Acrescenta a ausência mantendo a lista em ordem de início. */
export function withBlackout(member: Member, blackout: Blackout): Member {
  return {
    ...member,
    blackouts: [...member.blackouts, blackout].sort((a, b) => a.from.localeCompare(b.from)),
  };
}

export function withoutBlackout(member: Member, blackoutId: string): Member {
  return { ...member, blackouts: member.blackouts.filter((b) => b.id !== blackoutId) };
}

// ===== Dados complementares, vida na igreja, endereço e contato de emergência =====

export const CIVIL_STATUS_LABEL: Record<CivilStatus, string> = {
  single: 'Solteiro(a)',
  married: 'Casado(a)',
  stableUnion: 'União estável',
  divorced: 'Divorciado(a)',
  widowed: 'Viúvo(a)',
};

export const CHURCH_STATUS_LABEL: Record<ChurchStatus, string> = {
  active: 'Membro em comunhão',
  congregant: 'Congregado',
  away: 'Afastado',
  transferring: 'Em transferência',
};

/** As situações na ordem de quem escolhe: a mais provável primeiro. */
export const CHURCH_STATUS_ORDER: readonly ChurchStatus[] = ['active', 'congregant', 'transferring', 'away'];

/** O que cada situação quer dizer, em uma linha (mostrado embaixo da opção, na lista e no select). */
export const CHURCH_STATUS_HINT: Record<ChurchStatus, string> = {
  active: 'Já é membro desta igreja e participa normalmente.',
  congregant: 'Frequenta a igreja, mas ainda não é membro.',
  transferring: 'É membro de outra igreja e está vindo para esta.',
  away: 'É membro, mas está sem frequentar a igreja há um tempo.',
};

export const JOINED_VIA_LABEL: Record<JoinedVia, string> = {
  baptism: 'Batismo',
  profession: 'Profissão de fé',
  transfer: 'Transferência',
  acclamation: 'Aclamação',
  reconciliation: 'Reconciliação',
};

/** O que cada forma de ingresso quer dizer, em uma linha (mostrado embaixo da opção no select). */
export const JOINED_VIA_HINT: Record<JoinedVia, string> = {
  baptism: 'Tornou-se membro ao ser batizado nesta igreja.',
  profession: 'Já era batizado e entrou ao declarar publicamente a sua fé.',
  transfer: 'Veio de outra igreja, com a carta de transferência.',
  acclamation: 'Veio de outra igreja e foi recebido pela igreja, sem a carta.',
  reconciliation: 'Estava afastado e voltou à comunhão da igreja.',
};

/** Parentescos sugeridos no contato de emergência (o campo aceita qualquer texto). */
export const RELATION_SUGGESTIONS = ['Cônjuge', 'Mãe', 'Pai', 'Filho(a)', 'Irmão(ã)', 'Amigo(a)'] as const;

/** As 27 unidades da federação, em ordem alfabética. */
export const UFS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const;

export const EMPTY_ADDRESS: Address = {
  cep: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  uf: '',
};

export const EMPTY_EMERGENCY: EmergencyContact = { name: '', relation: '', phone: '' };

/** Tamanho máximo do nome de apresentação (o do crachá). */
export const MAX_NICKNAME = 40;

/** Os campos de cadastro além do básico, vazios — o que o membro novo ou importado leva. */
export const EMPTY_PROFILE = {
  civilStatus: null,
  profession: '',
  churchStatus: null,
  memberSince: null,
  joinedVia: null,
  baptismDate: null,
  originChurch: '',
  cpf: '',
  address: EMPTY_ADDRESS,
  emergency: EMPTY_EMERGENCY,
  location: null,
} as const satisfies Pick<
  Member,
  | 'civilStatus'
  | 'profession'
  | 'churchStatus'
  | 'memberSince'
  | 'joinedVia'
  | 'baptismDate'
  | 'originChurch'
  | 'cpf'
  | 'address'
  | 'emergency'
  | 'location'
>;

/** "Igreja de origem" só aparece em caso de transferência ou aclamação. */
export function showsOriginChurch(joinedVia: JoinedVia | null): boolean {
  return joinedVia === 'transfer' || joinedVia === 'acclamation';
}

/** CEP como é guardado: só os 8 dígitos. */
export function cepDigits(cep: string): string {
  return cep.replace(/\D/g, '').slice(0, 8);
}

/** Máscara enquanto se digita: "01310-100". */
export function formatCep(cep: string): string {
  const d = cepDigits(cep);

  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** Erro do CEP: opcional, mas, se informado, com os 8 dígitos. `null` quando está válido. */
export function cepError(cep: string): string | null {
  const d = cepDigits(cep);

  if (!d) return null;
  if (d.length !== 8) return 'O CEP tem 8 dígitos.';

  return null;
}

/** O endereço como é guardado: CEP só com dígitos, UF em maiúsculas e sem espaço nas pontas. */
export function normalizeAddress(address: Address): Address {
  return {
    cep: cepDigits(address.cep),
    street: address.street.trim(),
    number: address.number.trim(),
    complement: address.complement.trim(),
    district: address.district.trim(),
    city: address.city.trim(),
    uf: address.uf.trim().toUpperCase(),
  };
}

/** O endereço tem algo preenchido? */
export function hasAddress(address: Address): boolean {
  return Object.values(address).some((v) => v.trim() !== '');
}

/** Endereço em uma linha: "Rua Augusta, 120 — apto 4 · Consolação · São Paulo/SP". */
export function addressLine(address: Address): string {
  const street = [address.street, address.number].filter(Boolean).join(', ');
  const first = [street, address.complement].filter(Boolean).join(' — ');
  const place = [address.city, address.uf].filter(Boolean).join('/');

  return [first, address.district, place].filter(Boolean).join(' · ');
}

/** UF reconhecida? (`''` também vale: não informada.) */
export function isUf(uf: string): boolean {
  return uf === '' || (UFS as readonly string[]).includes(uf.toUpperCase());
}

/**
 * Tempo de membresia a partir de "Membro desde": "Há 3 anos e 2 meses", "Há 5 meses",
 * "Há menos de um mês". `''` sem data ou com a data no futuro.
 */
export function membershipLabel(since: string | null, today: string): string {
  if (!since || since > today) return '';

  const [sy, sm, sd] = since.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  const months = (ty - sy) * 12 + (tm - sm) - (td < sd ? 1 : 0);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  if (months < 1) return 'Há menos de um mês';
  if (years === 0) return `Há ${plural(rest, 'mês', 'meses')}`;
  if (rest === 0) return `Há ${plural(years, 'ano', 'anos')}`;

  return `Há ${plural(years, 'ano', 'anos')} e ${plural(rest, 'mês', 'meses')}`;
}

/**
 * Erros dos campos de cadastro além do básico (o que o Salvar confere): datas no futuro,
 * CEP, UF e o telefone do contato de emergência. Chave = campo; vazio quando está tudo certo.
 */
export function memberProfileErrors(
  member: Pick<Member, 'memberSince' | 'baptismDate' | 'address' | 'emergency'>,
  today: string
): Partial<Record<'memberSince' | 'baptismDate' | 'cep' | 'uf' | 'emergencyPhone', string>> {
  const errors: Partial<Record<'memberSince' | 'baptismDate' | 'cep' | 'uf' | 'emergencyPhone', string>> = {};
  const cep = cepError(member.address.cep);
  const phone = memberPhoneError(member.emergency.phone);

  if (member.memberSince && member.memberSince > today) errors.memberSince = 'A data não pode ser no futuro.';
  if (member.baptismDate && member.baptismDate > today) errors.baptismDate = 'A data não pode ser no futuro.';
  if (cep) errors.cep = cep;
  if (!isUf(member.address.uf)) errors.uf = 'Escolha uma UF válida.';
  if (phone) errors.emergencyPhone = phone;

  return errors;
}

/** CPF, endereço e contato de emergência saem em branco para quem não pode vê-los. */
export function withoutPrivateData(member: Member): Member {
  return { ...member, cpf: '', address: EMPTY_ADDRESS, emergency: EMPTY_EMERGENCY, location: null };
}

/**
 * CPF como é guardado: só os dígitos. Não corta: com dígitos a mais, a validação recusa
 * (a planilha e a API não perdem nada em silêncio); quem corta é a máscara da digitação.
 */
export function cpfDigits(cpf: string): string {
  return cpf.replace(/\D/g, '');
}

/** Máscara enquanto se digita: "123.456.789-09", no máximo 11 dígitos. */
export function formatCpf(cpf: string): string {
  const d = cpfDigits(cpf).slice(0, 11);

  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;

  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * CPF válido: 11 dígitos, não todos iguais (000.000.000-00 passa no cálculo, mas não
 * existe) e com os dois dígitos verificadores certos (módulo 11 da Receita Federal).
 */
export function isValidCpf(cpf: string): boolean {
  const d = cpfDigits(cpf);

  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const check = (length: number) => {
    const sum = [...d.slice(0, length)].reduce((acc, digit, i) => acc + Number(digit) * (length + 1 - i), 0);
    const rest = (sum * 10) % 11;

    return rest === 10 ? 0 : rest;
  };

  return check(9) === Number(d[9]) && check(10) === Number(d[10]);
}

/**
 * Erro do CPF no cadastro: opcional, mas, se informado, válido e sem repetir o de outro
 * membro da organização. `null` quando está válido. Quem não vê os CPFs dos outros (o
 * próprio membro) só descobre a repetição no servidor, que confere com todos.
 */
export function memberCpfError(members: readonly Member[], memberId: string, cpf: string): string | null {
  const typed = cpfDigits(cpf);

  if (!typed) return null;
  if (typed.length !== 11) return 'O CPF tem 11 dígitos.';
  if (!isValidCpf(typed)) return 'Informe um CPF válido.';
  if (members.some((m) => m.id !== memberId && m.cpf === typed)) return 'Este CPF já é de outro membro.';

  return null;
}
